/**
 * Reprise de personnages existants (partie commencée sur un autre outil).
 * Le MJ colle un JSON ; on le complète avec une fiche vide et on vérifie les identifiants du référentiel,
 * sans appliquer les règles de création (XP déjà dépensée, génération, puissance du sang libres).
 */
import {ATTRIBUTE_IDS, SKILL_IDS} from './data/traits';
import {CLANS_BY_ID} from './data/clans';
import {DISCIPLINES_BY_ID, POWERS_BY_ID} from './data/disciplines';
import {PREDATORS_BY_ID} from './data/predators';
import {createEmptySheet, createVtmCharacter, ADVANTAGE_KIND, CHARACTER_STATUS} from './character';

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

// Objets fusionnés clé par clé avec la fiche vide ; le reste est remplacé tel quel.
const MERGED_FIELDS = ['attributes', 'skills', 'health', 'willpower', 'xp'];

const checkDots = (errors, label, values, ids, min, max) => {
  Object.entries(values).forEach(([id, value]) => {
    if (!ids.includes(id)) errors.push(`${label} inconnu : « ${id} »`);
    else if (!Number.isInteger(value) || value < min || value > max) errors.push(`${label} « ${id} » : ${value} (attendu ${min}-${max})`);
  });
};

const checkSheet = (sheet) => {
  const errors = [];
  if (sheet.clan && !CLANS_BY_ID[sheet.clan]) errors.push(`Clan inconnu : « ${sheet.clan} »`);
  if (sheet.predatorType && !PREDATORS_BY_ID[sheet.predatorType]) errors.push(`Prédateur inconnu : « ${sheet.predatorType} »`);
  checkDots(errors, 'Attribut', sheet.attributes, ATTRIBUTE_IDS, 1, 5);
  checkDots(errors, 'Compétence', sheet.skills, SKILL_IDS, 0, 5);
  sheet.specialties.forEach(item => {
    if (!SKILL_IDS.includes(item.skill)) errors.push(`Spécialité sur une compétence inconnue : « ${item.skill} »`);
  });
  Object.entries(sheet.disciplines).forEach(([id, discipline]) => {
    if (!DISCIPLINES_BY_ID[id]) {
      errors.push(`Discipline inconnue : « ${id} »`);
      return;
    }
    (discipline.powers || []).forEach(powerId => {
      const power = POWERS_BY_ID[powerId];
      if (!power) errors.push(`Pouvoir inconnu : « ${powerId} »`);
      else if (power.discipline !== id) errors.push(`Le pouvoir « ${powerId} » n'appartient pas à « ${id} »`);
    });
  });
  sheet.advantages.forEach(item => {
    if (!Object.values(ADVANTAGE_KIND).includes(item.kind)) errors.push(`Avantage « ${item.name} » : type « ${item.kind} » (attendu advantage ou flaw)`);
  });
  return errors;
};

/**
 * Construit le document d'un personnage repris.
 * @param {Object} raw {name, picture?, vtm: {...fiche partielle}}
 * @param {Object} ids {uid, idUser, idCampaign} — idUser = MJ tant que le personnage n'est pas attribué
 * @returns {{character: Object|null, errors: String[]}}
 */
export const buildImportedCharacter = (raw, {uid, idUser, idCampaign}) => {
  if (!isObject(raw)) return {character: null, errors: ['Personnage : un objet JSON est attendu']};
  if (!raw.name || typeof raw.name !== 'string') return {character: null, errors: ['Personnage sans nom']};
  const source = isObject(raw.vtm) ? raw.vtm : {};
  const empty = createEmptySheet();
  const sheet = Object.keys(empty).reduce((acc, key) => {
    if (!(key in source)) return acc;
    acc[key] = MERGED_FIELDS.includes(key) ? {...empty[key], ...source[key]} : source[key];
    return acc;
  }, {...empty});
  sheet.advantages = sheet.advantages.map(item => ({description: '', level: 1, source: 'creation', ...item}));

  const errors = checkSheet(sheet).map(message => `${raw.name} · ${message}`);
  const character = {
    ...createVtmCharacter({uid, name: raw.name.trim(), idUser, idCampaign, picture: raw.picture || null}),
    status: CHARACTER_STATUS.APPROVED,
    imported: true,
    vtm: sheet,
  };
  return {character, errors};
};

/**
 * Lit le texte collé par le MJ : un personnage ou un tableau de personnages.
 * @param {Function} nextUid générateur d'identifiants
 * @returns {{characters: Object[], errors: String[]}} aucun personnage si la moindre erreur
 */
export const parseImport = (text, {idUser, idCampaign, nextUid}) => {
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    return {characters: [], errors: [`JSON invalide : ${e.message}`]};
  }
  const list = Array.isArray(data) ? data : [data];
  if (list.length === 0) return {characters: [], errors: ['Aucun personnage']};
  const results = list.map(raw => buildImportedCharacter(raw, {uid: nextUid(), idUser, idCampaign}));
  const errors = results.flatMap(result => result.errors);
  return {characters: errors.length ? [] : results.map(result => result.character), errors};
};
